import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, Home, MessageCircle, Menu, NotebookPen } from 'lucide-react';
import { BottomNav, Fab, TabFrame, type NavItem } from '@/components/layout';
import { apiData } from '@/lib/api';
import { useCanWrite } from '@/store/auth';

/**
 * 빠른 작성 FAB의 라벨·목적지 — 지금 보는 화면에서 만드는 것과 같은 것을 만든다.
 * 어디서나 "알림장 쓰기"가 떠 있으면 관찰 탭에서 관찰 추가 버튼으로 오인된다.
 * 만들 대상이 분명하지 않은 화면(발달기록 탭·알림·쪽지·전체)에서는 띄우지 않는다.
 */
function fabFor(pathname: string): { label: string; to: string } | null {
  if (pathname === '/' || pathname === '/notes') {
    return { label: '알림장 쓰기', to: '/records/notes/new' };
  }
  if (pathname === '/observations') {
    return { label: '관찰 남기기', to: '/records/observations/new' };
  }
  // 발달기록(/development-tasks)은 태스크 목록에서 아이별로 들어간다
  return null;
}

/** 교사·원장 하단 탭 — 알림 / 쪽지 / 홈 / 전체 (§7.5 v1.8). 기록·소통은 전체 메뉴로. */
export function StaffShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const canWrite = useCanWrite();

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiData<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });

  const { data: messageUnread } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: () => apiData<{ count: number }>('/messages/unread-count'),
    refetchInterval: 60_000,
  });

  const items: NavItem[] = [
    { to: '/notifications', label: '알림', icon: <Bell size={20} />, badge: unread?.count },
    { to: '/messages', label: '쪽지', icon: <MessageCircle size={20} />, badge: messageUnread?.count },
    { to: '/', label: '홈', icon: <Home size={20} />, end: true },
    /* 교사·원장 모두 이동 중에 관찰을 바로 남길 수 있게 하단 탭에 둔다. 구독 만료 시설은 쓰기가 막히므로 감춘다. */
    ...(canWrite
      ? [{ to: '/records/observations/new', label: '관찰', icon: <NotebookPen size={20} /> }]
      : []),
    { to: '/more', label: '전체', icon: <Menu size={20} /> },
  ];

  const fab = fabFor(location.pathname);

  return (
    <TabFrame
      nav={<BottomNav items={items} />}
      /* 구독이 만료되면 새 기록을 쓸 수 없으므로 FAB을 감춘다 (§4 구독 게이트) */
      fab={canWrite && fab ? <Fab onClick={() => navigate(fab.to)} label={fab.label} /> : undefined}
    >
      <Outlet />
    </TabFrame>
  );
}
